import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Eye, 
  Download, 
  Filter, 
  Search, 
  Calendar,
  Clock,
  User,
  MapPin,
  Monitor,
  Lock,
  Unlock,
  Activity,
  BarChart3,
  RefreshCw,
  UserCheck,
  Users,
  Building,
  MoreVertical,
  Edit,
  Flag,
  CheckCircle2,
  X
} from 'lucide-react';
import api from '../../utils/api';

const ITEMS_PER_PAGE = 20;
const MAX_DISPLAY_ITEMS = 60;

const SecurityLogs = () => {
  const [securityEvents, setSecurityEvents] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('security');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    highSeverity: 0,
    mediumSeverity: 0,
    resolved: 0,
    activeSessions: 0
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [securityPage, setSecurityPage] = useState(1);
  const [accessPage, setAccessPage] = useState(1);

  // Fetch security events from API
  const fetchSecurityEvents = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        page: 1,
        limit: 100,
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : '',
        severity: severityFilter !== 'all' ? severityFilter : '',
        user_type: userTypeFilter !== 'all' ? userTypeFilter : ''
      });

      // Add date filtering
      if (customStartDate) {
        params.append('start_date', customStartDate);
      }
      if (customEndDate) {
        params.append('end_date', customEndDate);
      }

      const response = await api.get(`/api/security/events?${params}`);
      
      if (response.data.events) {
        let filteredEvents = response.data.events;
        
        // Apply client-side filtering for calculated fields
        if (statusFilter !== 'all') {
          filteredEvents = filteredEvents.filter(e => e.status === statusFilter);
        }
        if (severityFilter !== 'all') {
          filteredEvents = filteredEvents.filter(e => e.severity === severityFilter);
        }
        if (userTypeFilter !== 'all') {
          filteredEvents = filteredEvents.filter(e => e.user_type === userTypeFilter);
        }
        
        setSecurityEvents(filteredEvents);
        setSecurityPage(1);
        
        // Calculate stats from all events (not filtered)
        const highCount = response.data.events.filter(e => e.severity === 'high').length;
        const mediumCount = response.data.events.filter(e => e.severity === 'medium').length;
        const resolvedCount = response.data.events.filter(e => e.status === 'resolved').length;
        
        setStats({
          highSeverity: highCount,
          mediumSeverity: mediumCount,
          resolved: resolvedCount,
          activeSessions: 0 // Will be updated when access logs are fetched
        });
      }
    } catch (error) {
      console.error('Error fetching security events:', error);
      setError('Failed to fetch security events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch access logs from API
  const fetchAccessLogs = async () => {
    try {
      setError('');
      
      const params = new URLSearchParams({
        page: 1,
        limit: 100,
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : '',
        user_type: userTypeFilter !== 'all' ? userTypeFilter : ''
      });

      // Add date filtering
      if (customStartDate) {
        params.append('start_date', customStartDate);
      }
      if (customEndDate) {
        params.append('end_date', customEndDate);
      }

      const response = await api.get(`/api/security/access-logs?${params}`);
      
      if (response.data.accessLogs) {
        let filteredLogs = response.data.accessLogs;
        
        // Apply client-side filtering for calculated fields
        if (statusFilter !== 'all') {
          filteredLogs = filteredLogs.filter(log => log.status === statusFilter);
        }
        if (userTypeFilter !== 'all') {
          // Filter by portal_type: 'client' filter matches 'Client Portal', 'staff' matches 'Staff Portal'
          filteredLogs = filteredLogs.filter(log => {
            const portalType = log.portal_type || log.portalType || '';
            if (userTypeFilter === 'client') {
              return portalType === 'Client Portal' || portalType === 'client';
            } else if (userTypeFilter === 'staff') {
              return portalType === 'Staff Portal' || portalType === 'staff';
            }
            return true;
          });
        }
        
        setAccessLogs(filteredLogs);
        setAccessPage(1);
        
        // Update active sessions count from all logs (not filtered)
        const activeSessions = response.data.accessLogs.filter(
          log => log.status === 'success' && log.session_duration > 0
        ).length;
        
        setStats(prev => ({ ...prev, activeSessions }));
      }
    } catch (error) {
      console.error('Error fetching access logs:', error);
      setError('Failed to fetch access logs. Please try again.');
    }
  };

  // Update security event status
  const updateEventStatus = async (eventId, newStatus) => {
    try {
      setUpdatingStatus(true);
      
      // Update the local state immediately for better UX
      setSecurityEvents(prevEvents => 
        prevEvents.map(event => 
          event.id === eventId 
            ? { ...event, status: newStatus }
            : event
        )
      );
      
      // Update stats immediately
      setStats(prevStats => ({
        ...prevStats,
        resolved: newStatus === 'resolved' 
          ? prevStats.resolved + 1 
          : prevStats.resolved
      }));
      
      // Send update to server
      await api.put(`/api/security/events/${eventId}`, { status: newStatus });
      
      setOpenDropdown(null);
      setError('');
    } catch (error) {
      console.error('Error updating event status:', error);
      setError('Failed to update event status. Please try again.');
      
      // Revert the local state change on error
      setSecurityEvents(prevEvents => 
        prevEvents.map(event => 
          event.id === eventId 
            ? { ...event, status: event.status } // Keep original status
            : event
        )
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Export data to CSV
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      setError('No data to export');
      return;
    }

    const headers = selectedTab === 'security' 
      ? ['Event Type', 'Severity', 'Status', 'User', 'IP Address', 'Location', 'Description', 'Timestamp']
      : ['User', 'Action', 'Resource', 'IP Address', 'Location', 'Status', 'Duration', 'Timestamp'];

    const csvContent = [
      headers.join(','),
      ...data.map(item => {
        if (selectedTab === 'security') {
          const user = item.user_type === 'client' 
            ? (item.user ? `${item.user.firstName} ${item.user.lastName} (${item.user.companyName})` : 'N/A')
            : (item.staff ? `${item.staff.firstName} ${item.staff.lastName}` : 'N/A');
          
          return [
            item.event_type,
            item.severity,
            item.status,
            user,
            item.ip_address || 'N/A',
            item.location || 'N/A',
            `"${item.description}"`,
            new Date(item.created_at).toLocaleString()
          ].join(',');
        } else {
          const user = item.user_type === 'client' 
            ? (item.user ? `${item.user.firstName} ${item.user.lastName} (${item.user.companyName})` : 'N/A')
            : (item.staff ? `${item.staff.firstName} ${item.staff.lastName}` : 'N/A');
          
          return [
            user,
            item.action,
            item.resource,
            item.ip_address || 'N/A',
            item.location || 'N/A',
            item.status,
            item.session_duration ? `${Math.floor(item.session_duration / 60)}m` : 'N/A',
            new Date(item.created_at).toLocaleString()
          ].join(',');
        }
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handle date range changes
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    
    if (range === 'custom') {
      setShowCustomDates(true);
      // Set default custom dates to last 7 days
      const now = new Date();
      const startDate = new Date();
      startDate.setDate(now.getDate() - 7);
      setCustomStartDate(startDate.toISOString().split('T')[0]);
      setCustomEndDate(now.toISOString().split('T')[0]);
    } else {
      setShowCustomDates(false);
      const now = new Date();
      let startDate = new Date();
      
      switch (range) {
        case '1d':
          startDate.setDate(now.getDate() - 1);
          break;
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          break;
        default:
          startDate = new Date(0); // All time
      }
      
      setCustomStartDate(startDate.toISOString().split('T')[0]);
      setCustomEndDate(now.toISOString().split('T')[0]);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    fetchSecurityEvents();
    fetchAccessLogs();
  };

  // Apply filters
  const applyFilters = () => {
    fetchSecurityEvents();
    if (selectedTab === 'access') {
      fetchAccessLogs();
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSeverityFilter('all');
    setUserTypeFilter('all');
    setDateRange('7d');
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomDates(false);
  };

  // Handle dropdown toggle
  const toggleDropdown = (eventId) => {
    setOpenDropdown(openDropdown === eventId ? null : eventId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchSecurityEvents();
    fetchAccessLogs();
  }, []);

  // Apply filters when they change
  useEffect(() => {
    if (!loading) {
      applyFilters();
    }
  }, [selectedTab, searchTerm, statusFilter, severityFilter, userTypeFilter, dateRange, customStartDate, customEndDate]);

  const getSeverityBadge = (severity) => {
    const severityConfig = {
      critical: { color: 'bg-red-100 text-red-800', icon: AlertTriangle },
      high: { color: 'bg-red-100 text-red-800', icon: AlertTriangle },
      medium: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      low: { color: 'bg-green-100 text-green-800', icon: CheckCircle }
    };
    
    const config = severityConfig[severity] || severityConfig.low;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'Unknown'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      investigating: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      resolved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      pending: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      false_positive: { color: 'bg-gray-100 text-gray-800', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status ? status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1) : 'Unknown'}
      </span>
    );
  };

  const getActionBadge = (action) => {
    const actionConfig = {
      login: { color: 'bg-blue-100 text-blue-800' },
      logout: { color: 'bg-gray-100 text-gray-800' },
      view: { color: 'bg-green-100 text-green-800' },
      create: { color: 'bg-purple-100 text-purple-800' },
      update: { color: 'bg-orange-100 text-orange-800' },
      delete: { color: 'bg-red-100 text-red-800' },
      export: { color: 'bg-indigo-100 text-indigo-800' }
    };
    
    const config = actionConfig[action] || actionConfig.view;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {action ? action.charAt(0).toUpperCase() + action.slice(1) : 'Unknown'}
      </span>
    );
  };

  const getUserTypeBadge = (portalType) => {
    // Backend sends "Client Portal" or "Staff Portal" string
    const isClientPortal = portalType === 'Client Portal' || portalType === 'client';
    const config = isClientPortal
      ? { color: 'bg-blue-100 text-blue-800', icon: Building, text: 'Client Portal' }
      : { color: 'bg-purple-100 text-purple-800', icon: Users, text: 'Staff Portal' };
    
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return time.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const limitedSecurityEvents = securityEvents.slice(0, MAX_DISPLAY_ITEMS);
  const securityTotalPages = Math.max(1, Math.ceil(limitedSecurityEvents.length / ITEMS_PER_PAGE));
  const currentSecurityPage = Math.min(securityPage, securityTotalPages);
  const paginatedSecurityEvents = limitedSecurityEvents.slice(
    (currentSecurityPage - 1) * ITEMS_PER_PAGE,
    currentSecurityPage * ITEMS_PER_PAGE
  );
  const securityStartIndex = limitedSecurityEvents.length === 0 ? 0 : (currentSecurityPage - 1) * ITEMS_PER_PAGE + 1;
  const securityEndIndex = securityStartIndex === 0 ? 0 : securityStartIndex + paginatedSecurityEvents.length - 1;

  const limitedAccessLogs = accessLogs.slice(0, MAX_DISPLAY_ITEMS);
  const accessTotalPages = Math.max(1, Math.ceil(limitedAccessLogs.length / ITEMS_PER_PAGE));
  const currentAccessPage = Math.min(accessPage, accessTotalPages);
  const paginatedAccessLogs = limitedAccessLogs.slice(
    (currentAccessPage - 1) * ITEMS_PER_PAGE,
    currentAccessPage * ITEMS_PER_PAGE
  );
  const accessStartIndex = limitedAccessLogs.length === 0 ? 0 : (currentAccessPage - 1) * ITEMS_PER_PAGE + 1;
  const accessEndIndex = accessStartIndex === 0 ? 0 : accessStartIndex + paginatedAccessLogs.length - 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security & Logs</h1>
          <p className="text-gray-600 mt-1">Monitor security events and access logs across client and staff portals</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => exportToCSV(selectedTab === 'security' ? securityEvents : accessLogs, selectedTab === 'security' ? 'security_events' : 'access_logs')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="w-4 h-4 mr-2" />
            Export {selectedTab === 'security' ? 'Events' : 'Logs'}
          </button>
          <button 
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">High Severity</p>
              <p className="text-2xl font-bold text-gray-900">{stats.highSeverity}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Medium Severity</p>
              <p className="text-2xl font-bold text-gray-900">{stats.mediumSeverity}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">{stats.resolved}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeSessions}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setSelectedTab('security')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'security'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2" />
              Security Events
            </button>
            <button
              onClick={() => setSelectedTab('access')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'access'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Eye className="w-4 h-4 inline mr-2" />
              Access Logs
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Filters */}
          <div className="mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search events or logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
                  {selectedTab === 'security' ? (
                    <>
                      <option value="pending">Pending</option>
                      <option value="investigating">Investigating</option>
                      <option value="resolved">Resolved</option>
                      <option value="false_positive">False Positive</option>
                    </>
                  ) : (
                    <>
                      <option value="success">Success</option>
                      <option value="failed">Failed</option>
                      <option value="denied">Denied</option>
                    </>
                  )}
                </select>

                {/* Severity Filter (Security Events only) */}
                {selectedTab === 'security' && (
                  <select
                    value={severityFilter}
                    onChange={(e) => setSeverityFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Severities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                )}

                {/* User Type Filter */}
                <select
                  value={userTypeFilter}
                  onChange={(e) => setUserTypeFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Users</option>
                  <option value="client">Client Portal</option>
                  <option value="staff">Staff Portal</option>
                </select>
                
                {/* Date Range Filter */}
                <select
                  value={dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1d">Last 24 hours</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="custom">Custom Range</option>
                </select>

                {/* Custom Date Inputs */}
                {showCustomDates && (
                  <>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </>
                )}

                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                >
                  Apply Filters
                </button>

                <button
                  onClick={clearFilters}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Security Events Tab */}
          {selectedTab === 'security' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Security Events</h3>
              {securityEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No security events found for the selected filters.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Event
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Severity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP Address
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedSecurityEvents.map((event) => (
                          <tr key={event.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{event.description}</div>
                              <div className="text-sm text-gray-500">{(event.eventType || event.event_type || '').replace(/_/g, ' ')}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getSeverityBadge(event.severity)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getUserTypeBadge(event.userType || event.user_type)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {event.userName || event.user_name || 
                              ((event.userType || event.user_type) === 'client' 
                                ? (event.user ? `${event.user.firstName} ${event.user.lastName} (${event.user.companyName})` : 'N/A')
                                : (event.staff ? `${event.staff.firstName} ${event.staff.lastName}` : 'N/A')
                              )
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {event.ipAddress || event.ip_address || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <MapPin className="w-3 h-3 mr-1" />
                              {event.location || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(event.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatRelativeTime(event.createdAt || event.created_at || event.timestamp)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="relative dropdown-container">
                              <button
                                onClick={() => toggleDropdown(event.id)}
                                disabled={updatingStatus}
                                className="p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                              >
                                {updatingStatus ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                ) : (
                                  <MoreVertical className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                              
                              {openDropdown === event.id && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                  <div className="py-1">
                                    <button
                                      onClick={() => updateEventStatus(event.id, 'pending')}
                                      disabled={updatingStatus}
                                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <Clock className="w-4 h-4 mr-3 text-gray-400" />
                                      Mark as Pending
                                    </button>
                                    <button
                                      onClick={() => updateEventStatus(event.id, 'investigating')}
                                      disabled={updatingStatus}
                                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <Edit className="w-4 h-4 mr-3 text-blue-500" />
                                      Mark as Investigating
                                    </button>
                                    <button
                                      onClick={() => updateEventStatus(event.id, 'resolved')}
                                      disabled={updatingStatus}
                                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <CheckCircle2 className="w-4 h-4 mr-3 text-green-500" />
                                      Mark as Resolved
                                    </button>
                                    <button
                                      onClick={() => updateEventStatus(event.id, 'false_positive')}
                                      disabled={updatingStatus}
                                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <X className="w-4 h-4 mr-3 text-gray-500" />
                                      Mark as False Positive
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {limitedSecurityEvents.length > 0 && (
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between px-4 py-3 border rounded-lg border-gray-200 space-y-2 md:space-y-0">
                      <p className="text-sm text-gray-600">
                        Showing {securityStartIndex}-{securityEndIndex} of {limitedSecurityEvents.length}
                        {securityEvents.length > MAX_DISPLAY_ITEMS && ` (showing first ${MAX_DISPLAY_ITEMS})`}
                      </p>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSecurityPage(prev => Math.max(1, prev - 1))}
                          disabled={currentSecurityPage === 1}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {currentSecurityPage} of {securityTotalPages}
                        </span>
                        <button
                          onClick={() => setSecurityPage(prev => Math.min(securityTotalPages, prev + 1))}
                          disabled={currentSecurityPage === securityTotalPages}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Access Logs Tab */}
          {selectedTab === 'access' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Access Logs</h3>
              {accessLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No access logs found for the selected filters.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Resource
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP Address
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duration
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Time
                        </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedAccessLogs.map((log, index) => (
                          <tr key={log.id || `${log.user_id || 'log'}-${index}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getUserTypeBadge(log.portal_type || log.portalType)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {log.userName || log.user_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getActionBadge(log.action)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(log.resource || 'N/A').replace(/_/g, ' ')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {log.ipAddress || log.ip_address || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <MapPin className="w-3 h-3 mr-1" />
                              {log.location || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              log.status === 'success' 
                                ? 'bg-green-100 text-green-800' 
                                : log.status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {log.status === 'success' ? <CheckCircle className="w-3 h-3 mr-1" /> : 
                               log.status === 'failed' ? <XCircle className="w-3 h-3 mr-1" /> : 
                               <AlertTriangle className="w-3 h-3 mr-1" />}
                              {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(log.sessionDuration || log.session_duration || 0) > 0 ? `${Math.floor((log.sessionDuration || log.session_duration) / 60)}m` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatRelativeTime(log.createdAt || log.created_at || log.timestamp)}
                          </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {limitedAccessLogs.length > 0 && (
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between px-4 py-3 border rounded-lg border-gray-200 space-y-2 md:space-y-0">
                      <p className="text-sm text-gray-600">
                        Showing {accessStartIndex}-{accessEndIndex} of {limitedAccessLogs.length}
                        {accessLogs.length > MAX_DISPLAY_ITEMS && ` (showing first ${MAX_DISPLAY_ITEMS})`}
                      </p>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setAccessPage(prev => Math.max(1, prev - 1))}
                          disabled={currentAccessPage === 1}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {currentAccessPage} of {accessTotalPages}
                        </span>
                        <button
                          onClick={() => setAccessPage(prev => Math.min(accessTotalPages, prev + 1))}
                          disabled={currentAccessPage === accessTotalPages}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityLogs;
